import quebecFormat from "./quebecFormat";

const hasContent = (v) => v != null && v !== "" && !(Array.isArray(v) && !v.length);

// A photo's position & zoom (src/utils/framing.js) is stored beside the photo: "framing"
// next to a media/list item's url, or "<photo>Framing" next to a plain photo field.
// Returns the photo's key for a framing key, else null.
const framingPhotoKey = (key) => (key === "framing" ? "url" : /.Framing$/.test(key) ? key.slice(0, -7) : null);

// Merge a service page's French content over its English content, field by
// field. Empty French fields (and list items) fall back to English; lists
// follow the English order and length. French-only fields (filled in French
// but empty in English) are kept. A photo's framing follows the photo shown:
// with its own French photo, the French framing as a whole (or none); otherwise
// the English photo with the English framing.
function mergeFr(en, fr) {
  if (en == null && fr != null && typeof fr === "object") return clean(fr);
  if (Array.isArray(en)) {
    return en
      .map((item, i) => (item == null ? null : mergeFr(item, Array.isArray(fr) ? fr[i] : undefined)))
      .filter((item) => item != null);
  }
  if (en && typeof en === "object") {
    const frObj = fr && typeof fr === "object" && !Array.isArray(fr) ? fr : null;
    const ownFrPhoto = (photoKey) => !!frObj && hasContent(frObj[photoKey]);
    const out = {};
    for (const key of Object.keys(en)) {
      const photoKey = framingPhotoKey(key);
      if (!photoKey) out[key] = mergeFr(en[key], fr?.[key]);
      else if (!ownFrPhoto(photoKey)) out[key] = clean(en[key]);
      else if (hasContent(frObj[key])) out[key] = clean(frObj[key]);
    }
    if (frObj) {
      for (const key of Object.keys(frObj)) {
        if (key in en || !hasContent(frObj[key])) continue;
        const photoKey = framingPhotoKey(key);
        if (!photoKey || ownFrPhoto(photoKey)) out[key] = clean(frObj[key]);
      }
    }
    return out;
  }
  if (typeof fr === "string" && fr.trim()) return fr;
  // Numbers set in French (e.g. a framing's zoom) are kept as they are.
  if (typeof fr === "number" && Number.isFinite(fr)) return fr;
  return en;
}

// Drop null list items left by the editor (kept there to line EN/FR up).
function clean(value) {
  if (Array.isArray(value)) return value.filter((v) => v != null).map(clean);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, clean(v)]));
  }
  return value;
}

export default function localizeServicePage(page, isFR) {
  if (!page) return null;
  const en = page.en || {};
  let content = isFR ? mergeFr(en, page.fr || {}) : clean(en);
  // Stat numbers borrowed from English get Quebec typography ("2M+" → "2 M+").
  if (isFR && Array.isArray(en.stats)) {
    const frStats = page.fr?.stats || [];
    const enStats = en.stats.filter((s) => s != null);
    content = {
      ...content,
      stats: (content.stats || []).map((s, i) =>
        frStats[en.stats.indexOf(enStats[i])]?.value ? s : { ...s, value: quebecFormat(s.value) }
      ),
    };
  }
  return { content, shared: page.shared || {} };
}
